class CartDrawer extends DrawerComponent {
  constructor() {
    super();
    window.FoxKitSections = FoxTheme.utils.getSectionId(this);

    this.onCartRefreshListener = this.onCartRefresh.bind(this);
    this.getSectionToRenderListener = this.getSectionToRender.bind(this);
  }

  get requiresBodyAppended() {
    return false;
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('cart:grouped-sections', this.getSectionToRenderListener);
    document.addEventListener('cart:refresh', this.onCartRefreshListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:grouped-sections', this.getSectionToRenderListener);
    document.removeEventListener('cart:refresh', this.onCartRefreshListener);
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }

  show(focusElement = null, animate = true) {
    super.show(focusElement, animate);

    if (this.open) {
      FoxTheme.a11y.trapFocus(this, this.focusElement);
    }
  }

  async onCartRefresh(event) {
    const cartId = `CartDrawer-${this.sectionId}`;

    const cartElement = document.getElementById(cartId);
    if (!cartElement) return;

    try {
      const response = await fetch(`${FoxTheme.routes.root_url}?section_id=${this.sectionId}`);
      const responseText = await response.text();

      const parser = new DOMParser();
      const parsedHTML = parser.parseFromString(responseText, 'text/html');

      const refreshedCart = parsedHTML.getElementById(cartId);
      if (!refreshedCart) return;
      const newCartContent = refreshedCart.innerHTML;
      cartElement.innerHTML = newCartContent;

      if (event.detail.open === true) {
        this.show();
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }
}
customElements.define('cart-drawer', CartDrawer);

class CartItems extends HTMLElement {
  constructor() {
    super();

    console.log("CartItems constructor");

    this.addEventListener('change', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate.bind(this)
    );

    this.cartItemProducts = this.querySelectorAll('.cart-item__product');
    this.cartItemQuantitys = this.querySelectorAll('.cart-item__quantity');

    const loadTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const template = element.querySelector('template');
        const templateContent = template && document.importNode(template.content, true);
        const parent = element.querySelector(parentSelector);

        if (parent && template && !parent.querySelector('.template-content')) {
          const contentWrapper = document.createElement('div');
          contentWrapper.classList.add('template-content', 'w-full');
          contentWrapper.appendChild(templateContent);
          parent.appendChild(contentWrapper);
        }
      });
    };

    const removeTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const parent = element.querySelector(parentSelector);
        const templateContent = parent?.querySelector('.template-content');

        if (templateContent) {
          parent.removeChild(templateContent);
        }
      });
    };

    const handleTabletMatch = () => {
      loadTemplateContent(this.cartItemProducts, '.cart-item__product--info');
      removeTemplateContent(this.cartItemQuantitys, '.cart-item__quantity-wrapper');
    };

    const handleTabletUnmatch = () => {
      removeTemplateContent(this.cartItemProducts, '.cart-item__product--info');
      loadTemplateContent(this.cartItemQuantitys, '.cart-item__quantity-wrapper');
    };

    const mqlTablet = window.matchMedia(FoxTheme.config.mediaQueryTablet);
    FoxTheme.config.mqlTablet = mqlTablet.matches;

    if (FoxTheme.config.mqlTablet) {
      handleTabletMatch();
    } else {
      handleTabletUnmatch();
    }

    mqlTablet.onchange = (event) => {
      if (event.matches) {
        handleTabletMatch();
      } else {
        FoxTheme.config.mqlTablet = false;
        handleTabletUnmatch();
      }
    };

    window.FoxKitSections = FoxTheme.utils.getSectionId(this);
  }

  cartUpdateUnsubscriber = undefined;

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  setValidity(target, message) {
    target.setCustomValidity(message);
    target.reportValidity();
    target.value = target.defaultValue;
    target.select();
  }

  validateQuantity(event) {
    const target = event.target;
    const inputValue = parseInt(target.value);
    const index = target.getAttribute('data-index');
    let message = '';

    if (inputValue < parseInt(target.getAttribute('data-min'))) {
      message = FoxTheme.quickOrderListStrings.min_error.replace('[min]', target.getAttribute('data-min'));
    } else if (inputValue > parseInt(target.max)) {
      message = FoxTheme.quickOrderListStrings.max_error.replace('[max]', target.max);
    } else if (inputValue % parseInt(target.step) !== 0) {
      message = FoxTheme.quickOrderListStrings.step_error.replace('[step]', target.step);
    }

    if (message) {
      this.setValidity(target, message);
    } else {
      target.setCustomValidity('');
      target.reportValidity();
      this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
    }
  }

  async onCartUpdate(event) {
    if (event.cart.errors) {
      this.onCartError(event.cart.errors, event.target);
      return;
    }

    try {
      await window.BromicCanadaPlatinumGiftReady;
    } catch (error) {
      console.error('Canada gift initialization failed', error);
    }
    let cart = event.cart;
    if (window.BromicCanadaPlatinumGiftCart) {
      try {
        cart = (await window.BromicCanadaPlatinumGiftCart.reconcile()) || cart;
      } catch (error) {
        console.error('Canada gift reconciliation failed', error);
      }
    }

    const sectionId = FoxTheme.utils.getSectionId(this);
    let sectionHtml = cart.sections && cart.sections[sectionId];
    if (cart !== event.cart || !sectionHtml) {
      try {
        const response = await fetch(`${FoxTheme.routes.root_url}?section_id=${sectionId}`);
        sectionHtml = await response.text();
      } catch (error) {
        console.error('Error refreshing cart section:', error);
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        return;
      }
    }
    const sectionToRender = new DOMParser().parseFromString(sectionHtml, 'text/html');

    const cartDrawer = document.querySelector(`#CartDrawer-${sectionId}`);
    const cartDrawerBody = document.querySelector(`#CartDrawerBody-${sectionId}`);
    const cartDrawerFooter = document.querySelector(`#CartDrawerFooter-${sectionId}`);
    const cartDrawerEmpty = document.querySelector(`#CartDrawerEmpty-${sectionId}`);
    if (cartDrawer) {
      const updatedElement = sectionToRender.querySelector(`#CartDrawer-${sectionId}`);
      if (updatedElement) {
        cartDrawer.innerHTML = updatedElement.innerHTML;
        // CrescoSpec - auto delete assurance when cart empty (only has assurance)
        var script = updatedElement.querySelector("script#auto-delete-assurance-when-cart-empty");
        if(script){
          var newScript = document.createElement("script");
          // copy attributes
          [...script.attributes].forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });
          // copy inline script content
          newScript.textContent = script.textContent;
          cartDrawer.appendChild(newScript);
        }
        // end CrescoSpec - auto delete assurance when cart empty (only has assurance)
      }
      const cartDrawerBodyUpdate = sectionToRender.querySelector(`#CartDrawerBody-${sectionId}`);
      const cartDrawerFooterUpdate = sectionToRender.querySelector(`#CartDrawerFooter-${sectionId}`);
      const cartDrawerEmptyUpdate = sectionToRender.querySelector(`#CartDrawerEmpty-${sectionId}`);

      if (cartDrawerBodyUpdate) {
        cartDrawerBody.innerHTML = cartDrawerBodyUpdate.innerHTML;
      }
      if (cartDrawerFooterUpdate) {
        cartDrawerFooter.innerHTML = cartDrawerFooterUpdate.innerHTML;
      }

      if (cartDrawerEmptyUpdate) {
        cartDrawerEmpty.innerHTML = cartDrawerEmptyUpdate.innerHTML;
      }

      if (cart.item_count > 0) {
        cartDrawerBody.classList.remove('hidden');
        cartDrawerFooter.classList.remove('hidden');
        cartDrawerEmpty.classList.add('hidden');
      } else {
        cartDrawerBody.classList.add('hidden');
        cartDrawerFooter.classList.add('hidden');
        cartDrawerEmpty.classList.remove('hidden');
      }
    }

    const mainCart = document.querySelector(`#MainCart-${sectionId}`);
    if (mainCart) {
      const updatedElement = sectionToRender.querySelector(`#MainCart-${sectionId}`);

      if (updatedElement) {
        mainCart.innerHTML = updatedElement.innerHTML;
        // CrescoSpec - auto delete assurance when cart empty (only has assurance)
        const script = updatedElement.querySelector("script#auto-delete-assurance-when-cart-empty");
        if(script){
          var newScript = document.createElement("script");
          // copy attributes
          [...script.attributes].forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });
          // copy inline script content
          newScript.textContent = script.textContent;
          mainCart.appendChild(newScript);
        }
        // end CrescoSpec - auto delete assurance when cart empty (only has assurance)

      } else {
        mainCart.closest('.cart').classList.add('is-empty');
        mainCart.remove();
      }
    }

    const lineItem =
      document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);

    if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
      FoxTheme.a11y.trapFocus(mainCart || cartDrawer, lineItem.querySelector(`[name="${event.name}"]`));
    } else if (cart.item_count === 0) {
      cartDrawer
        ? FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector('a'))
        : FoxTheme.a11y.trapFocus(document.querySelector('.cart__empty'), document.querySelector('a'));
    } else {
      cartDrawer
        ? FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector('.cart-item__title'))
        : FoxTheme.a11y.trapFocus(mainCart, mainCart.querySelector('.cart-item__title'));
    }

    // The Canada reconciler is the sole owner of automatic controller gifts.
    function discountValidated() {
      return false;
    };

    // VERSION 1: used for multiple but sometimes combined promotions
    async function checkBuyXgetY(el, data) {
      //Holiday Bundle - Platinum Smart-Heat® Electric & FREE Control (the trigger)
      const discount_product_id = 8467556958375;

      //Platinum Smart-Heat® Electric Marine (the trigger)
      const discount_product_id_2 = 8467621249191;

      //Affinity Dimmer Controller (the gift)
      const discounted_variant_id = 46571318083751;
      
      //count the discounted product in the cart
      const discounted_variant_id_count = data.items.filter(item=>item.variant_id == discounted_variant_id).reduce((sum, item) => sum + item.quantity, 0);
      
      //count the discount product in the cart
      const discount_product_id_count = data.items.filter(item=>item.product_id == discount_product_id).reduce((sum, item) => sum + item.quantity, 0);
      const discount_product_id_2_count = data.items.filter(item=>item.product_id == discount_product_id_2).reduce((sum, item) => sum + item.quantity, 0);
      
      //check if there is 2 or more of each discount_product_id (1&2), add 1 discounted_variant_id
      if((discount_product_id_count >= 2 && discount_product_id_2_count < 2 && discounted_variant_id_count == 0) || (discount_product_id_count < 2 && discount_product_id_2_count >= 2 && discounted_variant_id_count == 0) || (discount_product_id_count >= 2 && discount_product_id_2_count >= 2 && discounted_variant_id_count == 1)){
        el.addItemToCart(discounted_variant_id, 1, document.activeElement.getAttribute('name'), event.target, data.items.length + 1);
      };

      // if(discount_product_id_count >= 2 && discounted_variant_id_count == 0){
      //   el.addItemToCart(discounted_variant_id, 1, document.activeElement.getAttribute('name'), event.target, data.items.length + 1);
      // };

      if(discount_product_id_count < 2 && discount_product_id_2_count < 2 && discounted_variant_id_count > 0) {
        var discount_variant_id_line = 1;

        for (const item of data.items) {
          if(item.variant_id === discounted_variant_id){
            break;
          }
          discount_variant_id_line = discount_variant_id_line + 1;
        }

        el.updateQuantity(discount_variant_id_line, 0, document.activeElement.getAttribute('name'), event.target);
      }
    };

    // VERSION 2: used for multiple, separated promotions
    async function checkBuyXgetY2(el, data, promotions) {
      promotions.forEach(promotion => {
        var discounted_variant_id = promotion.gift_variant_id;
        var discount_variant_ids = promotion.trigger_variant_ids;

        //count the discount product in the cart
        var discount_product_id_count = data.items
          .filter((item) => discount_variant_ids.includes(item.variant_id))
          .reduce((sum, item) => sum + item.quantity, 0);

        //count the discounted product in the cart
        var discounted_variant_id_count = data.items
          .filter((item) => item.variant_id == discounted_variant_id)
          .reduce((sum, item) => sum + item.quantity, 0);
                
        //check if there is 2 or more variants that are in trigger_variant_ids, add 1 discounted_variant_id
        if (discount_product_id_count >= 2 && discounted_variant_id_count == 0) {
          el.addItemToCart(discounted_variant_id, 1, document.activeElement.getAttribute('name'), event.target, data.items.length + 1);
        }

        //check if there are less than 2 variants that are in trigger_variant_ids, remove 1 discounted_variant_id
        if(discount_product_id_count < 2 && discounted_variant_id_count > 0) {
          var discounted_variant_id_line = 1;
          var discounted_variant_id_quantity;

          for (const item of data.items) {
            if(item.variant_id === discounted_variant_id){
              discounted_variant_id_quantity = item.quantity;
              break;
            }
            discounted_variant_id_line = discounted_variant_id_line + 1;
          }

          el.updateQuantity(discounted_variant_id_line, (discounted_variant_id_quantity - 1), document.activeElement.getAttribute('name'), event.target);
        }
      });
    };
    // end CrescoSpec - buyXgetY discount automation code

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart,
        },
      })
    );
  }

  onCartError(errors, target) {
    if (target) {
      this.updateQuantity(
        target.getAttribute('data-index'),
        target.defaultValue,
        document.activeElement.getAttribute('name'),
        target
      );
    } else {
      window.location.href = FoxTheme.routes.cart_url;
    }

    alert(errors);
  }

  updateQuantity(line, quantity, name, target) {
    this.showLoader(line);

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const body = JSON.stringify({
      line,
      quantity,
      sections: sectionsToBundle,
    });

    fetch(`${FoxTheme.routes.cart_change_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } })
    .then((response) => response.json())
    .then((parsedState) => {
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        sources: 'cart-items',
        cart: parsedState,
        target,
        line,
        name,
      });
    })
    .catch((error) => {
      console.log(error);
    });
  }

  addItemToCart = (variant_id, quantity, name, target, new_line_index) => {
    // this.showLoader(new_line_index);

    const body = JSON.stringify({
      items: [{
        id: variant_id,
        quantity
      }]
    });

    fetch(`${FoxTheme.routes.cart_add_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } })
    .then((response) => response.json())
    .then((parsedState) => {
      location.reload();
    })
    .catch((error) => {
      console.log(error);
    });
  }

  showLoader(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    if (loaders) {
      loaders.forEach((loader) => {
        loader.classList.add('btn--loading');
      });
    }
  }
}

customElements.define('cart-items', CartItems);

class CartRemoveItem extends HTMLAnchorElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();

      const cartItems = this.closest('cart-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-item', CartRemoveItem, { extends: 'a' });

class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', FoxTheme.utils.debounce(this.onChange.bind(this), 300));

    if (this.button && this.cartNoteDetailsSummary) {
      this.button.addEventListener('click', () => {
        this.cartNoteDetailsSummary.close();
      });
    }
  }

  get cartNoteDetailsSummary() {
    return this.closest('[is="accordion-details"]');
  }

  get button() {
    return this.querySelector('[type="button"]');
  }

  onChange(event) {
    const body = JSON.stringify({ note: event.target.value });
    fetch(`${FoxTheme.routes.cart_update_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } });
  }
}
customElements.define('cart-note', CartNote);

class CalculateShipping extends ModalComponent {
  constructor() {
    super();
    this.countryProvince = this.querySelector('country-province');
    this.isCountrySetup = false;
  }
  static get observedAttributes() {
    return [...super.observedAttributes, 'data-show'];
  }
  show() {
    super.show();
    this.setAttribute('data-show', true);
  }

  hide() {
    super.hide();
    this.setAttribute('data-show', false);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === 'data-show' && newValue === 'true' && !this.isCountrySetup && this.countryProvince) {
      const template = this.countryProvince.querySelector('template');
      const templateContent = document.importNode(template.content, true);
      this.countryProvince.countryElement.appendChild(templateContent);
      this.countryProvince.init();
      this.isCountrySetup = true;
    }
  }
}
customElements.define('calculate-shipping', CalculateShipping);

class CountryProvinceForm extends HTMLElement {
  constructor() {
    super();
    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement = this.querySelector('[name="address[country]"]');
    this.template = this.dataset.template;
    this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));
    if (this.template && this.template === 'cart') {
      this.init();
    }
  }

  init() {
    if (this.getAttribute('country') !== '') {
      this.countryElement.selectedIndex = Math.max(
        0,
        Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.dataset.country)
      );
      this.countryElement.dispatchEvent(new Event('change'));
    } else {
      this.handleCountryChange();
    }
  }

  handleCountryChange() {
    const option = this.countryElement.options[this.countryElement.selectedIndex],
      provinces = JSON.parse(option.dataset.provinces);

    this.provinceElement.closest('.form-field').hidden = provinces.length === 0;

    if (provinces.length === 0) {
      return;
    }

    this.provinceElement.innerHTML = '';

    provinces.forEach((data) => {
      const selected = data[1] === this.dataset.province;
      this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
    });
  }
}
customElements.define('country-province', CountryProvinceForm);

class ShippingCalculator extends HTMLFormElement {
  constructor() {
    super();

    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.lastElementChild;

    this.submitButton.addEventListener('click', this.handleFormSubmit.bind(this));
  }

  handleFormSubmit(event) {
    event.preventDefault();

    const zip = this.querySelector('[name="address[zip]"]').value,
      country = this.querySelector('[name="address[country]"]').value,
      province = this.querySelector('[name="address[province]"]').value;

    this.submitButton.classList.add('btn--loading');

    const body = JSON.stringify({
      shipping_address: { zip, country, province },
    });
    let sectionUrl = `${FoxTheme.routes.cart_url}/shipping_rates.json`;

    sectionUrl = sectionUrl.replace('//', '/');

    fetch(sectionUrl, { ...FoxTheme.utils.fetchConfig('javascript'), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.formatShippingRates(parsedState.shipping_rates);
        } else {
          this.formatError(parsedState);
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.resultsElement.hidden = false;
        this.submitButton.classList.remove('btn--loading');
      });
    
  }

  formatError(errors) {
    const shippingRatesList = Object.keys(errors).map((errorKey) => {
      return `<li>${errors[errorKey]}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert alert--error blocks-radius grid gap-2">
        <p class="font-body-bolder m-0">${FoxTheme.shippingCalculatorStrings.error}</p>
        <ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join('')}</ul>
      </div>
    `;
  }

  formatShippingRates(shippingRates) {
    const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
      return `<li>${presentment_name}: ${currency} ${price}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert blocks-radius alert--${
        shippingRates.length === 0 ? 'error' : 'success'
      } grid gap-2 leading-tight">
        <p class="font-body-bolder m-0">${
          shippingRates.length === 0
            ? FoxTheme.shippingCalculatorStrings.notFound
            : shippingRates.length === 1
            ? FoxTheme.shippingCalculatorStrings.oneResult
            : FoxTheme.shippingCalculatorStrings.multipleResults
        }</p>
        ${
          shippingRatesList === ''
            ? ''
            : `<ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join('')}</ul>`
        }
      </div>
    `;
  }
}
customElements.define('shipping-calculator', ShippingCalculator, { extends: 'form' });

class CartDrawerProductsRecommendation extends HTMLElement {
  constructor() {
    super();
    if ('requestIdleCallback' in window) {
      requestIdleCallback(this.init.bind(this), { timeout: 1500 });
    } else {
      const userAgent = navigator.userAgent;
      const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome');
      if (isSafari) {
        this.init();
      } else {
        FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
      }
    }
  }

  get slideContainer() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  init() {
    console.log("this.dataset.url", this.dataset.url);

    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        if(sectionInnerHTML === null)return;

        const recommendations = sectionInnerHTML.querySelector('cart-drawer-products-recommendation');

        if (recommendations && recommendations.innerHTML.trim().length) {
          const productCount = recommendations.querySelectorAll('.product-card');
          this.innerHTML = recommendations.innerHTML;
          this.initCarousel();
          if (productCount.length > 0) this.classList.remove('hidden');
          this.dispatchEvent(new CustomEvent('recommendations:loaded'));
        } else {
          this.closest('.shopify-section')?.remove();
          this.classList.add('hidden');
          this.dispatchEvent(new CustomEvent('is-empty'));
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  initCarousel() {
    this.carousel = new FoxTheme.Carousel(this.slideContainer, {
      slidesPerView: 1,
      spaceBetween: 10,
      loop: false,
      pagination: {
        el: this.sliderPagination,
        type: 'fraction',
      },
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
    });

    this.carousel && this.carousel.init();
  }
}
customElements.define('cart-drawer-products-recommendation', CartDrawerProductsRecommendation);

class MainCart extends HTMLElement {
  constructor() {
    super();

    document.addEventListener('cart:grouped-sections', this.getSectionToRender.bind(this));
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }
}
customElements.define('main-cart', MainCart);

class FreeShippingGoal extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      leftToSpend: '[data-left-to-spend]',
    };
    this.goal = Number(this.dataset.minimumAmount) * Number(window.Shopify.currency.rate || 1) || 0;
    this.progress = this.querySelector('progress-bar');
    this.money_format = window.FoxTheme.settings.moneyFormat;
  }

  connectedCallback() {
    this.updateShippingGloal(Number(this.dataset.cartTotal));
    document.addEventListener('cart:updated', (event) => {
      this.updateShippingGloal(event.detail.cart.items_subtotal_price);
    });
  }

  updateShippingGloal(amount) {
    if (amount > 0) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }

    this.cartTotal = amount / 100;
    this.goalLeft = this.goal - this.cartTotal;
    this.goalDone = this.goalLeft <= 0;

    this.percent = (this.cartTotal * 100) / this.goal;

    if (this.percent >= 100) this.percent = 100;

    if (this.cartTotal >= this.goal) {
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.classList.add('free-shipping-goal--done');
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    } else {
      let spend = (this.goal - this.cartTotal) * 100;
      this.querySelector(this.selectors.leftToSpend).innerHTML = FoxTheme.Currency.formatMoney(
        spend,
        this.money_format
      );
      this.classList.remove('free-shipping-goal--done');
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    }
  }
}
customElements.define('free-shipping-goal', FreeShippingGoal);

window.FoxKitAddToCart = async (payload) => {
  if (!payload?.properties?.['_FoxKit offer']) return;

  const cartJson = await (
    await fetch(`${FoxTheme.routes.cart_url}`, {
      ...FoxTheme.utils.fetchConfig(),
    })
  ).json();
  cartJson['sections'] = payload['sections'];
  FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });

  document.dispatchEvent(
    new CustomEvent('product-ajax:added', {
      detail: {
        product: payload,
      },
    })
  );
};
